import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { Link, Navigate, useLocation } from 'react-router-dom'

import { Alert } from '@/components/ui/Alert'
import { Button } from '@/components/ui/Button'
import { Card, CardBody } from '@/components/ui/Card'
import { Spinner } from '@/components/ui/Spinner'
import { useAuth } from '@/features/auth/store'
import { listMyOrders } from '@/features/marketplace/api'
import { useStorefrontSettings } from '@/features/marketplace/useStorefront'
import { OrderStatusBadge, PaymentStatusBadge } from '@/routes/storefront/StorefrontStatus'

/**
 * "My orders" (M24-05).
 *
 * Scoped by the server, not by a filter here: `GET /marketplace/orders` returns rows where
 * `user_id` is the caller's, so there is no customer id in this component to get wrong.
 * A shopper holds the `guest` role and has no permission on `orders` at all — ownership is
 * the entire authorisation story, and it lives in the query.
 */
export function StorefrontOrders() {
  const status = useAuth((s) => s.status)
  const location = useLocation()
  const [page, setPage] = useState(1)
  const { money } = useStorefrontSettings()

  const { data, isLoading, error } = useQuery({
    queryKey: ['storefront-orders', page],
    queryFn: () => listMyOrders(page),
    enabled: status === 'authenticated',
    retry: false,
  })

  if (status === 'loading') {
    return (
      <div className="flex justify-center py-16">
        <Spinner className="h-6 w-6 text-slate-400" />
      </div>
    )
  }

  if (status !== 'authenticated') {
    return <Navigate to="/store/login" state={{ from: location.pathname }} replace />
  }

  const orders = data?.data ?? []
  const meta = data?.meta

  return (
    <div className="space-y-4">
      <h1 className="text-xl font-semibold tracking-tight text-slate-900 dark:text-slate-100">
        My orders
      </h1>

      {error && <Alert tone="error" title="Could not load your orders." />}

      {isLoading ? (
        <div className="flex justify-center py-16">
          <Spinner className="h-6 w-6 text-slate-400" />
        </div>
      ) : orders.length === 0 ? (
        <Card>
          <CardBody className="space-y-4 py-12 text-center">
            <p className="text-sm text-slate-500 dark:text-slate-400">
              You have not ordered anything yet.
            </p>
            <Link to="/store" className="inline-block">
              <Button>Browse the shop</Button>
            </Link>
          </CardBody>
        </Card>
      ) : (
        <div className="space-y-3">
          {orders.map((order) => (
            <Link key={order.id} to={`/store/orders/${order.id}`} className="block">
              <Card className="transition-shadow hover:shadow-md">
                <CardBody className="flex flex-wrap items-center justify-between gap-3 p-4">
                  <div className="min-w-0">
                    <p className="font-medium text-slate-900 dark:text-slate-100">
                      {order.order_number}
                    </p>
                    <p className="text-xs text-slate-400">
                      {new Date(order.order_date).toLocaleDateString()}
                    </p>
                  </div>

                  <div className="flex items-center gap-2">
                    <OrderStatusBadge status={order.status} />
                    <PaymentStatusBadge status={order.payment_status} />
                  </div>

                  <span className="font-semibold text-slate-900 dark:text-slate-100">
                    {money(order.amount_due)}
                  </span>
                </CardBody>
              </Card>
            </Link>
          ))}
        </div>
      )}

      {meta && meta.total_pages > 1 && (
        <div className="flex items-center justify-center gap-3 pt-2">
          <Button
            variant="secondary"
            size="sm"
            disabled={meta.page <= 1}
            onClick={() => setPage((current) => current - 1)}
          >
            Previous
          </Button>
          <span className="text-sm text-slate-500 dark:text-slate-400">
            Page {meta.page} of {meta.total_pages}
          </span>
          <Button
            variant="secondary"
            size="sm"
            disabled={meta.page >= meta.total_pages}
            onClick={() => setPage((current) => current + 1)}
          >
            Next
          </Button>
        </div>
      )}
    </div>
  )
}
